import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api/v1';
const APP_TOKEN = process.env.REACT_APP_APPLICATION_TOKEN || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Interceptor: Use user token if logged in, else use application token
api.interceptors.request.use((config) => {
  const userData = localStorage.getItem('ph_user');
  let token = APP_TOKEN;

  if (userData) {
    try {
      const user = JSON.parse(userData);
      if (user && user.token) {
        token = user.token;
      }
    } catch (e) {
      // fallback to app token
    }
  }

  config.headers['User-Id'] = token;
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status } = error.response;
      if (status === 401) {
        localStorage.removeItem('ph_user');
        if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  me: () => api.get('/auth/me'),
};

// Jobs
export const jobsAPI = {
  list: (params) => api.get('/jobs', { params }),
  get: (id) => api.get(`/jobs/${id}`),
  create: (data) => api.post('/jobs', data),
  update: (id, data) => api.put(`/jobs/${id}`, data),
  delete: (id) => api.delete(`/jobs/${id}`),
  restore: (id) => api.put(`/jobs/${id}/restore`),
  search: (params) => api.get('/jobs/search', { params }),
  matchingCandidates: (id) => api.get(`/jobs/${id}/matching-candidates`),
};

// Applications
export const applicationsAPI = {
  list: (params) => api.get('/applications', { params }),
  apply: (data) => api.post('/applications', data),
  updateStatus: (id, data) => api.put(`/applications/${id}/status`, data),
  withdraw: (id) => api.delete(`/applications/${id}/withdraw`),
  getJobApplications: (jobId) => api.get(`/jobs/${jobId}/applications`),
};

// Companies
export const companiesAPI = {
  list: (params) => api.get('/companies', { params }),
  get: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
  restore: (id) => api.put(`/companies/${id}/restore`),
  employees: (id) => api.get(`/companies/${id}/employees`),
  addEmployee: (id, data) => api.post(`/companies/${id}/employees`, data),
  removeEmployee: (id, userId) => api.delete(`/companies/${id}/employees/${userId}`),
  reviews: (id) => api.get(`/companies/${id}/reviews`),
  addReview: (id, data) => api.post(`/companies/${id}/reviews`, data),
  follow: (id) => api.post(`/companies/${id}/follow`),
  unfollow: (id) => api.delete(`/companies/${id}/follow`),
};

// Dashboard
export const dashboardAPI = {
  candidate: () => api.get('/dashboard/candidate'),
  recruiter: () => api.get('/dashboard/recruiter'),
};

// Users
export const usersAPI = {
  list: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  restore: (id) => api.put(`/users/${id}/restore`),
  skills: (id) => api.get(`/users/${id}/skills`),
  addSkill: (id, data) => api.post(`/users/${id}/skills`, data),
  removeSkill: (id, skillId) => api.delete(`/users/${id}/skills/${skillId}`),
  experiences: (id) => api.get(`/users/${id}/experiences`),
  addExperience: (id, data) => api.post(`/users/${id}/experiences`, data),
  updateExperience: (id, expId, data) => api.put(`/users/${id}/experiences/${expId}`, data),
  deleteExperience: (id, expId) => api.delete(`/users/${id}/experiences/${expId}`),
  education: (id) => api.get(`/users/${id}/education`),
  addEducation: (id, data) => api.post(`/users/${id}/education`, data),
  updateEducation: (id, eduId, data) => api.put(`/users/${id}/education/${eduId}`, data),
  deleteEducation: (id, eduId) => api.delete(`/users/${id}/education/${eduId}`),
  uploadResume: (id, formData) => api.post(`/users/${id}/resume`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Skills
export const skillsAPI = {
  list: (params) => api.get('/skills', { params }),
  get: (id) => api.get(`/skills/${id}`),
  create: (data) => api.post('/skills', data),
};

// Locations
export const locationsAPI = {
  list: (params) => api.get('/locations', { params }),
  get: (id) => api.get(`/locations/${id}`),
  create: (data) => api.post('/locations', data),
};

// Categories
export const categoriesAPI = {
  list: (params) => api.get('/categories', { params }),
  get: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data),
};

// Roles
export const rolesAPI = {
  list: () => api.get('/roles'),
  get: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
  permissions: (id) => api.get(`/roles/${id}/permissions`),
  addPermission: (id, data) => api.post(`/roles/${id}/permissions`, data),
  removePermission: (id, permId) => api.delete(`/roles/${id}/permissions/${permId}`),
};

// Permissions
export const permissionsAPI = {
  list: () => api.get('/permissions'),
};

// Notifications
export const notificationsAPI = {
  list: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Job Alerts
export const jobAlertsAPI = {
  list: () => api.get('/job-alerts'),
  create: (data) => api.post('/job-alerts', data),
  update: (id, data) => api.put(`/job-alerts/${id}`, data),
  delete: (id) => api.delete(`/job-alerts/${id}`),
};

// Interviews
export const interviewsAPI = {
  list: (params) => api.get('/interviews', { params }),
  create: (data) => api.post('/interviews', data),
  update: (id, data) => api.put(`/interviews/${id}`, data),
  delete: (id) => api.delete(`/interviews/${id}`),
};

// Messages
export const messagesAPI = {
  list: (params) => api.get('/messages', { params }),
  send: (data) => api.post('/messages', data),
  get: (id) => api.get(`/messages/${id}`),
  delete: (id) => api.delete(`/messages/${id}`),
};

export default api;
